import React from 'react';
import PropTypes from 'prop-types';
import { Typography, Box, Alert, FormHelperText } from '@mui/material';

import * as yup from 'yup';
import Button from '../Button';
import Form from '../Form';
import Input from '../Input';

import { useStyles } from './ResetPasswordForm.styles';

// Min length 8 matches the Auth Hub shared password-reset contract. Server-
// side VALIDATION_ERROR responses for the password field are surfaced via
// the `passwordError` prop so users see the same constraint either way.
const FormSchema = yup.object().shape({
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters'),
  confirmPassword: yup
    .string()
    .required('Confirm password is required')
    .oneOf([yup.ref('password'), null], 'Passwords must match'),
});

export function ResetPasswordForm({
  onSubmit,
  isLoading,
  passwordError,
  bannerError,
}) {
  const classes = useStyles();

  return (
    <Box className={classes.root}>
      <Box className={classes.contents}>
        <Typography component="h1" variant="h5">
          Reset Password
        </Typography>
        {bannerError ? (
          <Alert
            severity="error"
            sx={{ mt: 2 }}
            data-testid="reset-password:banner-error"
          >
            {bannerError}
          </Alert>
        ) : null}
        <Form
          schema={FormSchema}
          className={classes.form}
          noValidate
          onSubmit={onSubmit}
          aria-label="reset password form"
        >
          <Input
            className={classes.input}
            variant="filled"
            margin="normal"
            required
            fullWidth
            name="password"
            label="New password"
            placeholder="Type your new password"
            type="password"
            id="password"
            autoComplete="new-password"
            inputProps={{ 'data-testid': 'reset-password:password-input' }}
          />
          {/*
            Server-side VALIDATION_ERROR for the password field is rendered
            here as an out-of-band sibling. Our Form wrapper injects
            error/helperText from the local yup schema and clobbers any
            error/helperText props passed to <Input> directly, so we cannot
            attach the server-side message inline on the input itself.
            FormHelperText with `error` keeps it visually adjacent and
            screen-reader-accessible.
          */}
          {passwordError ? (
            <FormHelperText
              error
              data-testid="reset-password:password-error"
              sx={{ mx: 1.75, mt: -1, mb: 1 }}
            >
              {passwordError}
            </FormHelperText>
          ) : null}
          <Input
            className={classes.input}
            variant="filled"
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm your new password"
            placeholder="Confirm your new password"
            type="password"
            id="confirmPassword"
            autoComplete="new-password"
            inputProps={{
              'data-testid': 'reset-password:confirm-password-input',
            }}
          />
          <Box display="flex" justifyContent="center">
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              loading={isLoading}
              data-testid="reset-password:button"
              aria-label="reset password button"
            >
              Set password
            </Button>
          </Box>
        </Form>
      </Box>
    </Box>
  );
}

ResetPasswordForm.propTypes = {
  onSubmit: PropTypes.func,
  isLoading: PropTypes.bool,
  passwordError: PropTypes.string,
  bannerError: PropTypes.string,
};
